/**
 * Improvement plans (PIP) — 90-day plans with 30/60/90 checkpoints.
 * Auto-drafted for band E on publish, startable from a review sheet, or
 * created standalone for anyone (e.g. a new joiner on probation).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  LifeBuoy, Loader2, ArrowLeft, Plus, CheckCircle2, XCircle, Trash2, Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls, todayISO } from '../../../components/admin/billing/shared';
import { Badge, statusLabel } from './shared';

const PIP_STATUS_STYLE = {
  draft: 'bg-slate-100 text-slate-600',
  open: 'bg-sky-100 text-sky-700',
  passed: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-red-100 text-red-700',
  closed: 'bg-slate-100 text-slate-500',
};
const CP_STYLE = {
  pending: 'bg-slate-100 text-slate-600 border-slate-200',
  met: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  missed: 'bg-red-50 text-red-700 border-red-200',
};

const PipsPage = () => {
  const { authHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [isHr, setIsHr] = useState(false);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [creating, setCreating] = useState(null); // { staff_id, start_date, objectives }
  const [drafts, setDrafts] = useState({});       // { [pipId]: { objectives, cp: {day:{status,notes}} } }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: opt }] = await Promise.all([
        axios.get(`${API}/performance/pips`, { headers: authHeader() }),
        axios.get(`${API}/performance/manager-options`, { headers: authHeader() }),
      ]);
      setRows(data.pips || []);
      setIsHr(!!data.is_hr);
      setStaff((opt.options || []).filter((o) => o.kind === 'staff'));
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const createPip = async () => {
    if (!creating?.staff_id) { toast.error('Pick a staff member'); return; }
    setBusy('create');
    try {
      await axios.post(`${API}/performance/pips`, {
        staff_id: creating.staff_id,
        start_date: creating.start_date || todayISO(),
        objectives: (creating.objectives || '').split('\n').filter(Boolean),
        status: 'open',
      }, { headers: authHeader() });
      toast.success('Improvement plan created');
      setCreating(null);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const saveObjectives = async (pip) => {
    setBusy(pip.id);
    try {
      await axios.patch(`${API}/performance/pips/${pip.id}`, {
        objectives: (drafts[pip.id]?.objectives ?? pip.objectives.join('\n')).split('\n').filter(Boolean),
      }, { headers: authHeader() });
      toast.success('Objectives saved');
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const setStatus = async (pip, status) => {
    setBusy(pip.id);
    try {
      await axios.patch(`${API}/performance/pips/${pip.id}`, { status }, { headers: authHeader() });
      toast.success(`Plan marked ${status}`);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const markCheckpoint = async (pip, day, status) => {
    setBusy(`${pip.id}-${day}`);
    try {
      await axios.post(`${API}/performance/pips/${pip.id}/checkpoint/${day}`, {
        status, notes: drafts[pip.id]?.cp?.[day] || '',
      }, { headers: authHeader() });
      toast.success(`${day}-day checkpoint marked ${status}`);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const removePip = async (pip) => {
    if (!window.confirm(`Delete the improvement plan for ${pip.staff_name}?`)) return;
    setBusy(pip.id);
    try {
      await axios.delete(`${API}/performance/pips/${pip.id}`, { headers: authHeader() });
      toast.success('Plan deleted');
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  return (
    <div className="space-y-5 pb-20" data-testid="pips-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/performance" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-1">
            <ArrowLeft size={14} /> Team scorecard
          </Link>
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <LifeBuoy size={20} className="text-teal-600" /> Improvement plans
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            90-day plans with 30 / 60 / 90-day checkpoints. Drafted automatically for band E.
          </p>
        </div>
        <button
          onClick={() => setCreating({ staff_id: '', start_date: todayISO(), objectives: '' })}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800"
          data-testid="new-pip-btn"
        >
          <Plus size={15} /> New plan
        </button>
      </div>

      {creating && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="new-pip-panel">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Staff member</label>
              <select
                className={inputCls} value={creating.staff_id}
                onChange={(e) => setCreating({ ...creating, staff_id: e.target.value })}
                data-testid="new-pip-staff"
              >
                <option value="">— Select —</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.sub}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Start date</label>
              <input
                type="date" className={inputCls} value={creating.start_date}
                onChange={(e) => setCreating({ ...creating, start_date: e.target.value })}
                data-testid="new-pip-start"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={createPip} disabled={busy === 'create'}
                className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm hover:bg-teal-700 disabled:opacity-50"
                data-testid="save-pip-btn"
              >
                {busy === 'create' ? <Loader2 size={15} className="animate-spin" /> : 'Create plan'}
              </button>
              <button onClick={() => setCreating(null)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-600">Cancel</button>
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Objectives (one per line)</label>
            <textarea
              rows={4} className={inputCls} value={creating.objectives}
              onChange={(e) => setCreating({ ...creating, objectives: e.target.value })}
              data-testid="new-pip-objectives"
            />
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-12 flex justify-center text-slate-400"><Loader2 className="animate-spin" /></div>
      ) : !rows.length ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-500" data-testid="no-pips">
          No improvement plans — nobody needs one right now.
        </div>
      ) : rows.map((p) => (
        <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`pip-${p.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="font-semibold text-slate-900">{p.staff_name}</div>
              <div className="text-xs text-slate-500">
                {p.start_date} → {p.end_date}
                {p.cycle_name ? ` · from ${p.cycle_name}` : ''}
                {p.band ? ` · band ${p.band}` : ''}
                {p.auto_created ? ' · auto-drafted' : ''}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={PIP_STATUS_STYLE[p.status]} testid={`pip-status-${p.id}`}>{statusLabel(p.status)}</Badge>
              {p.status !== 'closed' && (
                <button
                  onClick={() => setStatus(p, p.status === 'draft' ? 'open' : 'closed')}
                  disabled={busy === p.id}
                  className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid={`pip-toggle-${p.id}`}
                >
                  {p.status === 'draft' ? 'Activate' : 'Close'}
                </button>
              )}
              {isHr && (
                <button
                  onClick={() => removePip(p)} disabled={busy === p.id}
                  className="p-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                  data-testid={`pip-delete-${p.id}`}
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          </div>

          <div className="mt-3">
            <label className="block text-xs text-slate-500 mb-1">Objectives (one per line)</label>
            <textarea
              rows={Math.max(3, (p.objectives || []).length)}
              className={inputCls}
              value={drafts[p.id]?.objectives ?? (p.objectives || []).join('\n')}
              onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: { ...(d[p.id] || {}), objectives: e.target.value } }))}
              data-testid={`pip-objectives-${p.id}`}
            />
            <button
              onClick={() => saveObjectives(p)} disabled={busy === p.id}
              className="mt-2 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
              data-testid={`pip-save-objectives-${p.id}`}
            >
              <Save size={12} /> Save objectives
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 mt-4">
            {(p.checkpoints || []).map((c) => (
              <div key={c.day} className={`rounded-lg border p-3 ${CP_STYLE[c.status]}`} data-testid={`pip-cp-${p.id}-${c.day}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{c.day}-day</span>
                  <span className="text-[11px]">{c.due_date}</span>
                </div>
                <div className="text-[11px] font-medium mt-0.5 flex items-center gap-1">
                  {c.status === 'met' && <CheckCircle2 size={11} />}
                  {c.status === 'missed' && <XCircle size={11} />}
                  {statusLabel(c.status)}
                </div>
                <input
                  className={`${inputCls} mt-2 !text-xs`} placeholder="Checkpoint note"
                  value={drafts[p.id]?.cp?.[c.day] ?? c.notes ?? ''}
                  onChange={(e) => setDrafts((d) => ({
                    ...d,
                    [p.id]: { ...(d[p.id] || {}), cp: { ...(d[p.id]?.cp || {}), [c.day]: e.target.value } },
                  }))}
                  data-testid={`pip-cp-note-${p.id}-${c.day}`}
                />
                <div className="flex gap-1.5 mt-2">
                  <button
                    onClick={() => markCheckpoint(p, c.day, 'met')}
                    disabled={busy === `${p.id}-${c.day}`}
                    className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                    data-testid={`pip-cp-met-${p.id}-${c.day}`}
                  >
                    <CheckCircle2 size={11} /> Met
                  </button>
                  <button
                    onClick={() => markCheckpoint(p, c.day, 'missed')}
                    disabled={busy === `${p.id}-${c.day}`}
                    className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1.5 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                    data-testid={`pip-cp-missed-${p.id}-${c.day}`}
                  >
                    <XCircle size={11} /> Missed
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PipsPage;
